import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateInterviewQuestions } from '../services/openaiService';
import { createDoc, PDF, fR, T, hLine, drawCoverHeader, drawPageHeader, drawFooters, drawMetaCard, drawSectionHeading } from '../utils/pdfBrand';
import Header from './Header';
import Footer from './Footer';
import CreditsBadge from './CreditsBadge';
import './InterviewQuestionsPage.css';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const InterviewQuestionsPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user, credits, setCredits, fetchCredits } = useUser();
  
  const [jobDescription, setJobDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [error, setError] = useState('');
  const [parseError, setParseError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isResumeUploaded, setIsResumeUploaded] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [resumeChoice, setResumeChoice] = useState(null); // 'profile' | 'upload'

  // Interview-context fields — the model uses these to calibrate the
  // generated set. Sensible defaults so users who skip them still get
  // a reasonable result.
  const [interviewStage, setInterviewStage] = useState('Technical Round');
  const [interviewType, setInterviewType]   = useState('Mixed');
  const [experienceLevel, setExperienceLevel] = useState('Mid');
  const [difficulty, setDifficulty]         = useState('Standard');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  useEffect(() => {
    if (user) {
      fetch(`${API_BASE_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.profile) setProfileData(data.profile); })
        .catch(() => {});
    }
  }, [user, API_BASE_URL]);

  // Exit-confirm modal state. Replaces the native window.confirm() with
  // the branded "End the interview?" modal used elsewhere in the app.
  //   • showExitConfirm — controls the modal's visibility
  //   • exitTargetRef   — the element the candidate tried to click; we
  //                       re-fire the click on it after they confirm so
  //                       their original navigation actually happens
  //   • bypassGuardRef  — set briefly while we replay the click so our
  //                       own capture-phase handler lets the click
  //                       through this one time
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitTargetRef = useRef(null);
  const bypassGuardRef = useRef(false);

  // Session takeover — when used inside InterviewQuestionsLanding the
  // landing component owns the body class + scroll lock (it knows
  // whether the user has opened the generator). We skip toggling here
  // because two useEffects fighting over the same body class causes a
  // momentary flicker on every isGenerating/questions transition,
  // which made the form jump up + tuck under the header right after
  // clicking Generate. The `sessionActive` flag is still used below
  // for the navigation guard.
  const sessionActive = embedded && (isGenerating || questions.length > 0);

  // Navigation guard — clicks on Header / sidebar / profile menu, tab
  // close, and browser back all show a confirm so the candidate doesn't
  // accidentally lose 50 generated questions. Three layers, same as
  // MockInterviewPage:
  //   1. beforeunload (tab close / reload)
  //   2. document-level click capture (Header logo, sidebar items, etc.)
  //   3. popstate (browser back/forward)
  // Active only when there are actually questions to lose (not during
  // the empty-form state).
  useEffect(() => {
    if (!sessionActive) return;
    // beforeunload uses the browser's native confirm — we can't replace
    // it with a custom modal, but we keep the friendly copy.
    const CONFIRM_MSG = 'Your generated interview questions will be saved to your dashboard so you can return to them anytime.';

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = CONFIRM_MSG;
      return CONFIRM_MSG;
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    const onCaptureClick = (e) => {
      // If the modal-confirm flow is currently replaying the click,
      // let it through unchanged.
      if (bypassGuardRef.current) return;
      const target = e.target;
      if (!target || !target.closest) return;
      // Allow anything inside the studio (the generator + results panel).
      if (target.closest('.iql-studio')) return;
      if (target.closest('[data-allow-during-mock="true"]')) return;
      const anchor = target.closest('a[href]');
      const navBtn = target.closest('header, .header, nav, [role="navigation"], .product-sidebar, .header-avatar-img, .user-profile-container, .credits-display');
      if (!anchor && !navBtn) return;
      // Block the click and open the branded modal. We save the element
      // they tried to click so we can replay it on confirm.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      exitTargetRef.current = anchor || navBtn || target;
      setShowExitConfirm(true);
    };
    document.addEventListener('click', onCaptureClick, true);

    const onPopState = () => {
      // Re-pin the history entry and open the modal. exitTargetRef stays
      // null so the modal's confirm handler falls back to history.back().
      window.history.pushState(null, '', window.location.href);
      exitTargetRef.current = null;
      setShowExitConfirm(true);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onCaptureClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [sessionActive]);

  const buildResumeTextFromProfile = (profile) => {
    let text = '';
    if (profile.fullName) text += `${profile.fullName}\n`;
    if (profile.jobTitle) text += `${profile.jobTitle}\n`;
    if (profile.phone) text += `Phone: ${profile.phone}\n`;
    if (profile.location) text += `Location: ${profile.location}\n`;
    if (profile.linkedIn) text += `LinkedIn: ${profile.linkedIn}\n`;
    if (profile.github) text += `GitHub: ${profile.github}\n`;
    text += '\n';
    if (profile.professionalSummary) text += `PROFESSIONAL SUMMARY\n${profile.professionalSummary}\n\n`;
    if (profile.skills) text += `SKILLS\n${profile.skills}\n\n`;
    if (Array.isArray(profile.workExperience) && profile.workExperience.some(e => e.company || e.position)) {
      text += 'WORK EXPERIENCE\n';
      profile.workExperience.forEach(exp => {
        if (exp.position || exp.company) {
          text += `${exp.position || ''}${exp.company ? ` at ${exp.company}` : ''}${exp.duration ? ` (${exp.duration})` : ''}\n`;
          if (exp.description) text += `${exp.description}\n`;
          text += '\n';
        }
      });
    }
    if (Array.isArray(profile.education) && profile.education.some(e => e.degree || e.institution)) {
      text += 'EDUCATION\n';
      profile.education.forEach(edu => {
        if (edu.degree || edu.institution) {
          text += `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}${edu.institution ? ` - ${edu.institution}` : ''}${edu.year ? ` (${edu.year})` : ''}\n`;
        }
      });
      text += '\n';
    }
    if (Array.isArray(profile.projects) && profile.projects.some(p => p.name)) {
      text += 'PROJECTS\n';
      profile.projects.forEach(proj => {
        if (proj.name) {
          text += `${proj.name}${proj.technologies ? ` (${proj.technologies})` : ''}\n`;
          if (proj.description) text += `${proj.description}\n`;
          text += '\n';
        }
      });
    }
    if (profile.certifications) text += `CERTIFICATIONS\n${profile.certifications}\n`;
    return text.trim();
  };

  const handleJobDescriptionChange = (e) => {
    setJobDescription(e.target.value);
    setError('');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsParsingFile(true);
    setParseError('');
    setUploadedFileName(file.name);

    try {
      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        const reader = new FileReader();
        extractedText = await new Promise((resolve, reject) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsText(file);
        });
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          fullText += textContent.items.map(item => item.str).join(' ') + '\n';
        }
        extractedText = fullText.trim();
      } else if (
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.endsWith('.docx') ||
        file.type === 'application/msword' ||
        file.name.endsWith('.doc')
      ) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        extractedText = result.value;
      } else {
        throw new Error('Unsupported file format. Please upload .pdf, .docx, or .txt files.');
      }

      extractedText = extractedText.trim();
      if (!extractedText) throw new Error('No text could be extracted from the file.');

      setResumeText(extractedText);
      setIsResumeUploaded(true);
      setParseError('');
    } catch (error) {
      console.error('Error processing file:', error);
      setParseError(error.message || 'Failed to process file.');
      setIsResumeUploaded(false);
      setUploadedFileName('');
    } finally {
      setIsParsingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deductCredit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-questions/deduct-credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setShowInsufficientCreditsModal(true);
          return false;
        }
        throw new Error(data.error || 'Failed to deduct credit');
      }

      if (setCredits) {
        setCredits(data.remainingCredits);
      }
      
      return true;
    } catch (error) {
      console.error('Error deducting credit:', error);
      setError('Failed to process credit transaction');
      return false;
    }
  };

  const saveToDatabase = async (generatedQuestions) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview-questions/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          jobTitle,
          companyName,
          jobDescription,
          questions: generatedQuestions,
          resumeFilename: uploadedFileName
        })
      });

      if (response.ok) {
        console.log('Interview questions saved successfully');
      }
    } catch (saveError) {
      console.error('Error saving interview questions:', saveError);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!user) {
      setShowAuthModal(true);
      setError('Please login to generate interview questions');
      return;
    }

    if (!resumeText.trim()) {
      setError('Please upload your resume');
      return;
    }

    if (!jobDescription.trim()) {
      setError('Please enter the job description');
      return;
    }

    if (!jobTitle.trim()) {
      setError('Please enter the job title');
      return;
    }

    if (credits < 1) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsGenerating(true);
    setError('');
    setQuestions([]); // clear any prior results so the streaming append is clean

    try {
      const creditDeducted = await deductCredit();

      if (!creditDeducted) {
        setIsGenerating(false);
        return;
      }

      // Fixed total of 50 — Duration field removed from the form.
      // We fetch in batches of BATCH_SIZE for progressive UI; the
      // candidate sees questions appear as soon as each batch returns
      // instead of waiting for the whole set.
      const BATCH_SIZE = 5;
      const totalTarget = 50;

      const baseOptions = {
        interviewStage,
        interviewType,
        experienceLevel,
        difficulty,
      };

      let accumulated = [];
      while (accumulated.length < totalTarget) {
        const remaining = totalTarget - accumulated.length;
        const thisBatch = Math.min(BATCH_SIZE, remaining);

        // eslint-disable-next-line no-await-in-loop
        const batch = await generateInterviewQuestions(
          resumeText,
          jobDescription,
          jobTitle,
          companyName,
          {
            ...baseOptions,
            batchCount: thisBatch,
            existingQuestions: accumulated.map(q => q.question).filter(Boolean),
          }
        );

        if (!Array.isArray(batch) || batch.length === 0) {
          // Model returned nothing for this batch — stop here so the
          // user keeps whatever they have rather than spinning forever.
          break;
        }

        accumulated = [...accumulated, ...batch];
        setQuestions(accumulated); // progressive append → UI updates immediately
      }

      if (accumulated.length > 0) {
        // Persist the final set once streaming finishes.
        await saveToDatabase(accumulated);
      }

      if (fetchCredits) {
        fetchCredits();
      }
    } catch (error) {
      console.error('Error generating interview questions:', error);
      setError('Failed to generate interview questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClearAll = () => {
    setJobDescription('');
    setJobTitle('');
    setCompanyName('');
    setResumeText('');
    setUploadedFileName('');
    setQuestions([]);
    setError('');
    setParseError('');
    setIsResumeUploaded(false);
    setExpandedQuestions(new Set());
    setResumeChoice(null);
    // Reset interview context to defaults — same as the initial mount.
    setInterviewStage('Technical Round');
    setInterviewType('Mixed');
    setExperienceLevel('Mid');
    setDifficulty('Standard');
  };

  const toggleQuestion = (index) => {
    // Accordion behavior — only one question expanded at a time.
    // Clicking the open one closes it; clicking a different one
    // closes the previous one and opens the new one.
    setExpandedQuestions(prev => {
      if (prev.has(index)) return new Set();
      return new Set([index]);
    });
  };

  const handleCopyAll = () => {
    const allText = questions.map((q, i) => 
      `Question ${i + 1}: ${q.question}\n\nAnswer: ${q.answer}\n\n---\n\n`
    ).join('');
    navigator.clipboard.writeText(allText);
    alert('All questions and answers copied to clipboard!');
  };

  const handleDownloadPDF = () => {
    try {
      const doc = createDoc();
      const { W, H, M, CW } = PDF;
      const C = {
        ink:      [22, 22, 28],
        text:     [55, 55, 68],
        muted:    [115, 115, 130],
        hairline: [225, 225, 232],
        accent:   [109, 40, 217],
      };
      let y = 0;

      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      // White paper, page 1.
      fR(doc, 255, 255, 255, 0, 0, W, H);

      // Editorial cover header.
      drawCoverHeader(doc, {
        reportTitle: 'Interview Questions',
        role: jobTitle,
        company: companyName,
        dateStr,
      });

      // Line-height factor used by every multi-line draw below.
      doc.setLineHeightFactor(1.35);

      // Metadata block — eyebrow + clean two-column table.
      y = 108;
      doc.setCharSpace(1.2);
      T(doc, C.muted, 7, true, 'AT A GLANCE', M, y);
      doc.setCharSpace(0);
      y += 6;
      y = drawMetaCard(doc, [
        { label: 'Position', value: jobTitle },
        { label: 'Company', value: companyName },
        { label: 'Total Questions', value: String(questions.length) },
        { label: 'Prepared', value: dateStr },
      ], y) + 22;

      // Section heading for the questions list.
      y = drawSectionHeading(doc, `Interview Questions  ·  ${questions.length} items`, y);

      const addNewPage = () => {
        doc.addPage();
        fR(doc, 255, 255, 255, 0, 0, W, H);
        drawPageHeader(doc, jobTitle);
        y = 24;
        doc.setLineHeightFactor(1.35);
      };
      const checkBreak = (need) => { if (y + need > H - 22) addNewPage(); };

      // Per-line vertical advance (mm). At lineHeightFactor 1.35 the
      // actual rendered line heights are ~5.2mm at fontSize 11 and
      // ~4.5mm at fontSize 9.5 — we allocate a touch more so text never
      // collides with the next block.
      const Q_LINE = 5.4;   // question (fontSize 11, bold)
      const A_LINE = 4.6;   // answer (fontSize 9.5, regular)

      // Text column widths used both for wrapping AND for rendering —
      // computed once so wrap math always matches the available width.
      //   Question text starts at M+13 (after the Q01 mark) and ends
      //   at the page right margin → available = CW - 13.
      //   Answer text spans the full content column → available = CW.
      const Q_WIDTH = CW - 13;
      const A_WIDTH = CW;

      questions.forEach((q, index) => {
        // CRITICAL FIX: set the font to EXACTLY what we'll render with
        // BEFORE calling splitTextToSize, so wrap widths are computed
        // against the right metrics. Previously the wrap was computed
        // at whatever the last drawn font was (size 7 muted text),
        // which left the bold size-11 question text overflowing the
        // right edge of the page.
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        const qLines = doc.splitTextToSize(q.question || '', Q_WIDTH);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        const ansLines = doc.splitTextToSize(q.answer || '', A_WIDTH);

        const qH = qLines.length * Q_LINE;
        const aH = ansLines.length * A_LINE;
        const blockH = qH + 4 + 5 + 2 + aH + 12;
        checkBreak(blockH);

        // Q-number mark on the left, aligned with the first line.
        T(doc, C.accent, 9, true, `Q${String(index + 1).padStart(2, '0')}`, M, y + 3.6);

        // Question text — bold, near-black, multi-line.
        T(doc, C.ink, 11, true, qLines, M + 13, y + 3.6);
        y += qH + 3;

        // Hairline between question and answer.
        hLine(doc, M, y, M + CW, C.hairline[0], C.hairline[1], C.hairline[2], 0.3);
        y += 5;

        // "MODEL ANSWER" eyebrow.
        doc.setCharSpace(1.4);
        T(doc, C.accent, 6.5, true, 'MODEL ANSWER', M, y);
        doc.setCharSpace(0);
        y += 4;

        // Answer body.
        T(doc, C.text, 9.5, false, ansLines, M, y + 3.2);
        y += aH + 12;
      });

      drawFooters(doc);

      const fileName = `terview-questions-${(jobTitle || 'interview').replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className={embedded ? 'iq-embedded-root' : 'neural-glass-homepage'}>
      {!embedded && <div className="neural-background"></div>}

      {!embedded && (
        <div className="geometric-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
      )}

      <div
        className={`interview-questions-page-container ${embedded ? 'interview-questions-embedded' : ''}`}
        style={embedded ? undefined : { '--page-hero-img': "url('/images/page-heroes/interview-questions.png')" }}
      >
        {!embedded && <Header setShowAuthModal={setShowAuthModal} />}

        {showInsufficientCreditsModal && (
          <div className="modal-overlay" onClick={() => setShowInsufficientCreditsModal(false)}>
            <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
              <h3>Insufficient Credits</h3>
              <p>You need at least 1 credit to generate interview questions.</p>
              <p>Current credits: <strong>{credits}</strong></p>
              <div className="modal-actions">
                <button className="modal-btn primary" onClick={() => {
                  setShowInsufficientCreditsModal(false);
                  document.dispatchEvent(new CustomEvent('openPricingModal'));
                }}>
                  Get More Credits
                </button>
                <button className="modal-btn" onClick={() => setShowInsufficientCreditsModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="interview-questions-page-main">
          {!embedded && (
            <div className="li-hero">
              <div className="li-hero-badge">🚀 AI-Powered</div>
              <h1 className="li-hero-title">
                Interview <span className="li-gradient-text">Questions Generator</span>
              </h1>
              <p className="li-hero-subtitle">
                Train with AI-powered interview questions and prepare for your next interview.
              </p>
              <CreditsBadge feature="interviewQuestions" label="Interview Questions" className="hero-credits-badge" />
            </div>
          )}
          <div className="interview-questions-main-container">
            {/* hasResults flips on as soon as the user fires Generate
                (loading state) or any questions land. Before that the
                right panel is hidden and the form gets the full width
                centered. After it flips, the layout reflows to the
                two-column workspace. */}
            <div className={`interview-questions-content ${(isGenerating || questions.length > 0) ? 'has-results' : 'no-results'}`}>
              <div className="left-panel">
                <div className="job-details-section">
                  <div className="job-details-container glass">
                    <div className="section-header">
                      <h3>Job Details</h3>
                      <button 
                        onClick={handleClearAll} 
                        className="clear-button"
                        disabled={isGenerating || isParsingFile}
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="form-grid-iq">
                      <div className="form-group-iq">
                        <label>Job Title *</label>
                        <input
                          type="text"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          placeholder="e.g., Senior Software Engineer"
                          disabled={isGenerating || isParsingFile}
                        />
                      </div>
                      <div className="form-group-iq">
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="e.g., Tech Corp"
                          disabled={isGenerating || isParsingFile}
                        />
                      </div>

                      {/* Interview context — these four required selects
                          are the highest-leverage signals the model uses
                          to calibrate the question set. Defaults match
                          the most common interview profile so users who
                          ignore them still get a reasonable result. */}
                      <div className="form-group-iq">
                        <label>Interview Stage *</label>
                        <select
                          value={interviewStage}
                          onChange={(e) => setInterviewStage(e.target.value)}
                          disabled={isGenerating || isParsingFile}
                        >
                          <option>Phone Screen</option>
                          <option>Technical Round</option>
                          <option>Hiring Manager</option>
                          <option>System Design</option>
                          <option>Behavioral</option>
                          <option>Final / Loop</option>
                        </select>
                      </div>
                      <div className="form-group-iq">
                        <label>Interview Type *</label>
                        <select
                          value={interviewType}
                          onChange={(e) => setInterviewType(e.target.value)}
                          disabled={isGenerating || isParsingFile}
                        >
                          <option>Mixed</option>
                          <option>Technical</option>
                          <option>Behavioral</option>
                          <option>System Design</option>
                          <option>Coding</option>
                          <option>Cultural Fit</option>
                          <option>Case Study</option>
                        </select>
                      </div>
                      <div className="form-group-iq">
                        <label>Experience Level *</label>
                        <select
                          value={experienceLevel}
                          onChange={(e) => setExperienceLevel(e.target.value)}
                          disabled={isGenerating || isParsingFile}
                        >
                          <option>Entry</option>
                          <option>Mid</option>
                          <option>Senior</option>
                          <option>Staff</option>
                          <option>Principal</option>
                          <option>Manager</option>
                        </select>
                      </div>
                      <div className="form-group-iq">
                        <label>Difficulty *</label>
                        <select
                          value={difficulty}
                          onChange={(e) => setDifficulty(e.target.value)}
                          disabled={isGenerating || isParsingFile}
                        >
                          <option>Standard</option>
                          <option>Hard</option>
                          <option>FAANG-level</option>
                        </select>
                      </div>

                      <div className="form-group-iq full-width">
                        <label>Job Description *</label>
                        <textarea
                          value={jobDescription}
                          onChange={handleJobDescriptionChange}
                          placeholder="Paste the complete job description here..."
                          rows="8"
                          disabled={isGenerating || isParsingFile}
                        ></textarea>
                      </div>

                      <div className="form-group-iq full-width file-upload-inline">
                        <label className="form-group-iq" style={{ marginBottom: '8px' }}>Your Resume *</label>

                        {profileData && (profileData.professionalSummary || profileData.skills || (Array.isArray(profileData.workExperience) && profileData.workExperience.some(e => e.company || e.position))) ? (
                          <>
                            <div className="resume-choice-buttons-iq">
                              <button
                                type="button"
                                onClick={() => {
                                  setResumeChoice('profile');
                                  const text = buildResumeTextFromProfile(profileData);
                                  setResumeText(text);
                                  setUploadedFileName(`${profileData.fullName || 'Profile'} (Profile Resume)`);
                                  setIsResumeUploaded(true);
                                  setParseError('');
                                }}
                                className={`resume-choice-btn-iq${resumeChoice === 'profile' ? ' resume-choice-btn-iq-active' : ''}`}
                                disabled={isParsingFile || isGenerating}
                              >
                                <div className="resume-choice-label-iq">✅ Use Profile Resume</div>
                                {profileData.fullName && <div className="resume-choice-sublabel-iq">{profileData.fullName}</div>}
                              </button>
                              {/* Hidden file input — always mounted so we can
                                  trigger it directly from the card click.
                                  Removes the intermediate "Upload Resume"
                                  button step the user used to see. */}
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.docx,.doc"
                                onChange={handleFileUpload}
                                disabled={isParsingFile || isGenerating}
                                style={{ display: 'none' }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  // Mark choice, clear any prior file state,
                                  // and open the OS file picker on the same
                                  // click — no second click required.
                                  setResumeChoice('upload');
                                  setResumeText('');
                                  setUploadedFileName('');
                                  setIsResumeUploaded(false);
                                  fileInputRef.current?.click();
                                }}
                                className={`resume-choice-btn-iq${resumeChoice === 'upload' ? ' resume-choice-btn-iq-active' : ''}`}
                                disabled={isParsingFile || isGenerating}
                              >
                                <div className="resume-choice-label-iq">📄 Upload New Resume</div>
                                <div className="resume-choice-sublabel-iq">
                                  {isParsingFile
                                    ? 'Processing…'
                                    : uploadedFileName
                                      ? `✓ ${uploadedFileName}`
                                      : 'Click to pick a PDF or DOCX'}
                                </div>
                              </button>
                            </div>

                            {resumeChoice === 'profile' && (
                              <div className="profile-resume-confirm-iq">
                                ✓ Profile resume loaded and ready to use.
                              </div>
                            )}

                            {resumeChoice === 'upload' && uploadedFileName && (
                              <div className="profile-resume-confirm-iq">
                                ✓ {uploadedFileName} loaded and ready to use.
                              </div>
                            )}

                            {!resumeChoice && (
                              <p className="upload-hint-iq">Select an option above to provide your resume</p>
                            )}
                          </>
                        ) : (
                          <div className="upload-area-iq">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".pdf,.docx,.doc"
                              onChange={handleFileUpload}
                              disabled={isParsingFile || isGenerating}
                              style={{ display: 'none' }}
                            />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="file-upload-label-iq"
                              disabled={isParsingFile || isGenerating}
                            >
                              {isParsingFile ? (
                                <span className="loading">
                                  <span className="typing-indicator"><span></span><span></span><span></span></span>
                                  Processing Resume...
                                </span>
                              ) : 'Upload Resume (PDF/DOCX)'}
                            </button>
                            <p className="upload-hint-iq">
                              {uploadedFileName ? `✓ ${uploadedFileName}` : 'Upload your resume to generate personalized questions'}
                            </p>
                          </div>
                        )}

                        {parseError && <div className="parse-error-iq">{parseError}</div>}

                        {resumeChoice !== null && (
                          <div className="form-group-iq full-width resume-data" style={{ marginTop: '10px' }}>
                            <textarea
                              value={resumeText}
                              onChange={(e) => setResumeText(e.target.value)}
                              placeholder="Resume content will appear here after upload..."
                              rows="8"
                              disabled={isGenerating || isParsingFile}
                            ></textarea>
                          </div>
                        )}

                        <div className="generate-section">
                          <button
                            onClick={handleGenerateQuestions}
                            className="generate-btn-main"
                            disabled={isGenerating || !isResumeUploaded || !resumeText.trim() || !jobDescription.trim() || !jobTitle.trim() || !user}
                          >
                            {isGenerating ? (
                              <span className="loading">
                                <span className="typing-indicator">
                                  <span></span><span></span><span></span>
                                </span>
                                Generating Questions...
                              </span>
                            ) : !user ? 'Login to Generate Questions' : !isResumeUploaded ? 'Upload Resume to Generate' : 'Generate Interview Questions (1 Credit)'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {(isGenerating || questions.length > 0) && (
              <div className="right-panel">
                <div className="questions-output-section">
                  <div className="questions-container glass">
                    <div className="section-header">
                      <h3>Interview Questions ({questions.length})</h3>
                      <div className="action-buttons">
                        <button 
                          onClick={handleCopyAll} 
                          className="copy-button"
                          disabled={isGenerating || questions.length === 0}
                        >
                          Copy All
                        </button>
                        <button 
                          onClick={handleDownloadPDF} 
                          className="download-button"
                          disabled={isGenerating || questions.length === 0}
                        >
                          Download PDF
                        </button>
                      </div>
                    </div>
                    <div className="questions-list">
                      {/* Streaming-friendly render order:
                          • If any questions exist (even mid-generation),
                            render them so the candidate sees each one
                            appear as soon as its batch returns.
                          • If we're still generating, append a small
                            progress card at the bottom indicating more
                            are on the way.
                          • Full-screen "Generating…" placeholder only
                            shows when isGenerating AND nothing has
                            streamed back yet (the brief moment between
                            click and first batch).
                          • Empty-state quick-steps only when idle. */}
                      {questions.length > 0 ? (
                        <>
                          {questions.map((q, index) => (
                            <div key={index} className="question-item">
                              <div
                                className="question-header"
                                onClick={() => toggleQuestion(index)}
                              >
                                <span className="question-number">Q{index + 1}</span>
                                <span className="question-text">{q.question}</span>
                                <span className="expand-icon">
                                  {expandedQuestions.has(index) ? '−' : '+'}
                                </span>
                              </div>
                              {expandedQuestions.has(index) && (
                                <div className="answer-section">
                                  <strong>Answer:</strong>
                                  <p>{q.answer}</p>
                                </div>
                              )}
                            </div>
                          ))}
                          {isGenerating && (
                            <div className="streaming-progress">
                              <span className="typing-indicator">
                                <span></span><span></span><span></span>
                              </span>
                              Generating question {questions.length + 1} of 50…
                            </div>
                          )}
                        </>
                      ) : isGenerating ? (
                        <div className="loading-placeholder">
                          <span className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </span>
                          Generating your personalized interview questions…
                        </div>
                      ) : (
                        <div className="placeholder">
                          Your personalized interview questions will appear here...
                          <br /><br />
                          <small>Quick steps:</small>
                          <ul>
                            <li>Fill in job details and description</li>
                            <li>Upload your resume</li>
                            <li>Click "Generate Interview Questions"</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>

            {error && (
              <div className="error-message glass">
                {error}
              </div>
            )}
          </div>

          {!embedded && (
          <>
          <section className="info-section glass">
            <h2 className="section-title">Why Use Our Interview Question Generator?</h2>
            <div className="info-grid">
              <div className="info-card glass">
                <div className="info-icon">🎯</div>
                <h3>Personalized Questions</h3>
                <p>AI analyzes your resume and job description to generate 50 tailored interview questions specific to your background and the role you're applying for.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">💡</div>
                <h3>Smart Answers</h3>
                <p>Each question comes with a detailed answer based on your actual experience, helping you prepare confident responses for your interview.</p>
              </div>
              <div className="info-card glass">
                <div className="info-icon">⚡</div>
                <h3>Interview Ready</h3>
                <p>Practice with realistic questions covering technical skills, behavioral scenarios, and role-specific topics to ace your interview.</p>
              </div>
            </div>
          </section>

          <section className="how-it-works glass">
            <h2 className="section-title">How It Works</h2>
            <div className="steps-grid">
              <div className="step-card glass">
                <div className="step-number">1</div>
                <h4>Enter Job Details</h4>
                <p>Provide the job title, company name, and paste the complete job description.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">2</div>
                <h4>Upload Your Resume</h4>
                <p>Upload your resume so our AI can understand your experience and skills.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">3</div>
                <h4>Generate Questions</h4>
                <p>Our AI creates 50 personalized interview questions with tailored answers.</p>
              </div>
              <div className="step-card glass">
                <div className="step-number">4</div>
                <h4>Practice & Prepare</h4>
                <p>Review questions and answers to prepare for your upcoming interview.</p>
              </div>
            </div>
          </section>

          {/* tips-section */}
          <section className="tips-section glass">
            <h2 className="section-title">Interview Preparation Tips</h2>
            <div className="tips-grid">
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Practice Out Loud</h4>
                </div>
                <p>Don't just read the answers mentally. Practice speaking them out loud to build confidence and improve delivery.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Customize Your Answers</h4>
                </div>
                <p>Use the generated answers as a foundation, then personalize them with your own voice and additional examples.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Research the Company</h4>
                </div>
                <p>Supplement these questions with company-specific research to show genuine interest in the organization.</p>
              </div>
              <div className="tip-card glass">
                <div className="tip-header">
                  <span className="tip-icon">✓</span>
                  <h4>Prepare Questions</h4>
                </div>
                <p>Have thoughtful questions ready to ask the interviewer about the role, team, and company culture.</p>
              </div>
            </div>
          </section>
          </>
          )}
        </main>

        {!embedded && <Footer />}
      </div>

      {/* Exit-confirm modal — branded "End the session?" prompt that
          replaces window.confirm(). Portaled to body so it lives above
          everything regardless of where the page is mounted (standalone
          route or embedded inside the landing). */}
      {showExitConfirm && createPortal(
        <div
          className="iqp-exit-overlay"
          onClick={() => { setShowExitConfirm(false); exitTargetRef.current = null; }}
          role="presentation"
        >
          <div
            className="iqp-exit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="iqp-exit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="iqp-exit-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 id="iqp-exit-title" className="iqp-exit-title">Leave the generator?</h3>
            <p className="iqp-exit-body">
              Leaving this page will close the question generator. Your
              generated questions will be saved to your dashboard so you can
              return to them anytime.
            </p>
            <div className="iqp-exit-actions">
              <button
                type="button"
                className="iqp-exit-btn iqp-exit-cancel"
                onClick={() => { setShowExitConfirm(false); exitTargetRef.current = null; }}
              >
                Stay
              </button>
              <button
                type="button"
                className="iqp-exit-btn iqp-exit-confirm"
                onClick={() => {
                  setShowExitConfirm(false);
                  const target = exitTargetRef.current;
                  exitTargetRef.current = null;
                  // Flag the guard to let the next click through, then
                  // replay the candidate's original click. For popstate
                  // (no target) fall back to history.back().
                  bypassGuardRef.current = true;
                  if (target && typeof target.click === 'function') {
                    target.click();
                  } else {
                    window.history.back();
                  }
                  setTimeout(() => { bypassGuardRef.current = false; }, 200);
                }}
              >
                Yes, leave
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InterviewQuestionsPage;